import axios from 'axios';
// Directory services
export const directoryService = {
  list: async ({ page=1, page_size=50, q='', project_id } = {}) => {
    const params = { page, page_size };
    if (q) params.q = q;
    if (project_id) params.project_id = project_id;
    const response = await api.get('/directory', { params });
    const total = parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0', 10);
    const totalPages = parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '1', 10);
    return { items: response.data, total, totalPages, page, pageSize: page_size };
  }
};


// API URL detection with fallback support
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  
  // Pre all-hands.dev - použiť work-2 backend na porte 12001
  if (hostname.includes('all-hands.dev')) {
    // Dynamicky odvodíme cluster id z hostname (work-1-<cluster>.prod-runtime...)
    const m = hostname.match(/^work-\d+-([^.]+)\./);
    const cluster = m && m[1] ? m[1] : null;
    if (cluster) {
      return `https://work-2-${cluster}.prod-runtime.all-hands.dev/api`;
    }
    return '/api';
  }
  
  // Pre localhost použijeme same-origin proxy /api (cez frontend server)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api';
  }

  // PRE IP ADRESU 192.168.0.111 POUŽIŤ ROVNAKÚ IP S PORTOM 12001
  if (hostname === '192.168.0.111') {
    return `http://${hostname}:12001/api`;
  }

  // Pre produkčné domény použiť same-origin /api proxy.
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration and network errors with fallback
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle authentication errors (401 = no token, 403 = invalid/expired token)
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(error);
    }
    
    // Handle network errors - try fallback to proxy if direct IP fails
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      const hostname = window.location.hostname;
      
      // If we're trying hardcoded IP and it fails, try proxy fallback
      if (error.config.baseURL === 'http://192.168.0.111:12001/api' && 
          (hostname === 'localhost' || hostname === '127.0.0.1')) {
        
        // Create new request with proxy URL
        const fallbackConfig = {
          ...error.config,
          baseURL: '/api',
          url: error.config.url.replace('http://192.168.0.111:12001/api', '')
        };
        
        try {
          return await axios(fallbackConfig);
        } catch (fallbackError) {
          return Promise.reject(fallbackError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
};

// User services
export const userService = {
  getAll: async () => {
    const response = await api.get('/users', { params: { page_size: 200 } });
    return response.data;
  },

  getAllPaged: async ({ page = 1, page_size = 20, q = '' } = {}) => {
    const response = await api.get('/users', { params: { page, page_size, q } });
    const total = parseInt(response.headers['x-total-count'] || '0', 10);
    const totalPages = parseInt(response.headers['x-total-pages'] || '1', 10);
    return { items: response.data, total, page, pageSize: page_size, totalPages };
  },
  
  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  
  getDeleteImpact: async (id) => {
    const response = await api.get(`/users/${id}/delete-impact`, {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache' }
    });
    return response.data;
  },

  delete: async (id, options = {}) => {
    const response = await api.delete(`/users/${id}`, {
      params: options.force ? { force: 'true' } : undefined
    });
    return response.data;
  },
  
  changePassword: async (id, passwordData) => {
    const response = await api.put(`/users/${id}/password`, passwordData);
    return response.data;
  },
  
  block: async (id, blocked) => {
    const response = await api.put(`/users/${id}/block`, { blocked });
    return response.data;
  },

  addBillingRate: async (id, rateData) => {
    const response = await api.post(`/users/${id}/billing-rates`, rateData);
    return response.data;
  },

  
  getTermsHistory: async (id) => {
    const response = await api.get(`/users/${id}/terms-history`);
    return response.data;
  },
};

// Project services
export const projectService = {
  getAll: async () => {
    const response = await api.get('/projects');
    return response.data;
  },

  getAllPaged: async ({ page = 1, page_size = 20, q = '' } = {}) => {
    const response = await api.get('/projects', { params: { page, page_size, q } });
    const total = parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || response.headers['X-total-count'] || '0', 10);
    const totalPages = parseInt(response.headers['x-total-pages'] || '1', 10);
    return { items: response.data, total, page, pageSize: page_size, totalPages };
  },
  
  getById: async (id) => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },
  
  create: async (projectData) => {
    const response = await api.post('/projects', projectData);
    return response.data;
  },
  
  update: async (id, projectData) => {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },
  
  getUsers: async (id) => {
    const response = await api.get(`/projects/${id}/users`);
    return response.data;
  },
  
  getRotation: async (id, month, year) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    
    const response = await api.get(`/projects/${id}/rotation`, { params });
    return response.data;
  },
  
  saveRotation: async (id, rotationData) => {
    const response = await api.post(`/projects/${id}/rotation`, rotationData);
    return response.data;
  },
};

// Rotation services
export const devTaskService = {
  list: async ({ page=1, page_size=20, q='', status='' } = {}) => {
    const response = await api.get('/dev-tasks', { params: { page, page_size, q, status } });
    return { items: response.data, total: parseInt(response.headers['x-total-count']||'0',10) };
  },
  create: async (payload) => {
    const { data } = await api.post('/dev-tasks', payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.put(`/dev-tasks/${id}`, payload);
    return data;
  },
  remove: async (id) => {
    const { data } = await api.delete(`/dev-tasks/${id}`);
    return data;
  },
};

export const queueService = {
  health: async () => {
    const { data } = await api.get('/queue/health');
    return data;
  },
  triggerMonthlyReports: async () => {
    const { data } = await api.post('/trigger-monthly-reports');
    return data;
  }
};

export const rotationService = {
  getProjectRotations: async (projectId, startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/rotations/project/${projectId}?${params}`);
    return response.data;
  },
  
  getUserRotations: async (userId, projectId) => {
    const endpoint = `/rotations/user/${userId}/project/${projectId}`;
    const response = await api.get(endpoint);
    return response.data;
  },
  
  saveUserRotation: async (userId, projectId, rotationData) => {
    const response = await api.post(`/rotations/user/${userId}/project/${projectId}`, rotationData);
    return response.data;
  },

  copyWeek: async (payload) => {
    const response = await api.post('/rotations/copy-week', payload);
    return response.data;
  },

  
  // Removed getUserRotationData - now using getUserRotations with project_rotations table
};

// Attendance services
export const attendanceService = {
  getAll: async (params = {}) => {
    const response = await api.get('/attendance', { params });
    return response.data;
  },
  
  getMy: async (params = {}) => {
    const response = await api.get('/attendance/my', { params });
    return response.data;
  },
  
  getByUser: async (userId, params = {}) => {
    const response = await api.get('/attendance', { params: { ...params, user_id: userId } });
    return response.data;
  },
  
  create: async (attendanceData) => {
    const response = await api.post('/attendance', attendanceData);
    return response.data;
  },
  
  update: async (id, attendanceData) => {
    const response = await api.put(`/attendance/${id}`, attendanceData);
    return response.data;
  },
  
  adminUpdate: async (id, adminData) => {
    const response = await api.put(`/attendance/${id}/admin`, adminData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/attendance/${id}`);
    return response.data;
  },
  
  // Absence management
  createAbsence: async (absenceData) => {
    const response = await api.post('/attendance/absence', absenceData);
    return response.data;
  },
  
  updateAbsence: async (id, absenceData) => {
    const response = await api.put(`/attendance/${id}/absence`, absenceData);
    return response.data;
  },
  
  deleteAbsence: async (id) => {
    const response = await api.delete(`/attendance/${id}/absence`);
    return response.data;
  },
};

// Vacation request services
export const vacationService = {
  getAll: async () => {
    const response = await api.get('/vacation-requests');
    return response.data;
  },
  
  create: async (requestData) => {
    const response = await api.post('/vacation-requests', requestData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await api.put(`/vacation-requests/${id}`, updateData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/vacation-requests/${id}`);
    return response.data;
  },
};

// Dashboard services
export const dashboardService = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  getMySummary: async () => {
    const response = await api.get('/dashboard/my-summary');
    return response.data;
  },

  getActionItems: async () => {
    const response = await api.get('/dashboard/action-items');
    return response.data;
  },

  getRecentActivity: async (limit = 10) => {
    const response = await api.get(`/dashboard/recent-activity?limit=${limit}`);
    const data = response.data;
    return Array.isArray(data) ? data : [];
  },
};

// Notification services
export const notificationService = {
  getAll: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },

  markAsRead: async (id) => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  deleteAll: async () => {
    const response = await api.delete('/notifications');
    return response.data;
  },
};

// Audit log services
export const auditLogService = {
  list: async (params = {}) => {
    const response = await api.get('/audit-logs', { params });
    return response.data;
  },
};

// Project Work Tasks service
export const projectTasksService = {
  list: async (projectId) => {
    const { data } = await api.get(`/projects/${projectId}/work-tasks`);
    return data;
  },
  create: async (projectId, payload) => {
    const { data } = await api.post(`/projects/${projectId}/work-tasks`, payload);
    return data;
  },
  update: async (projectId, id, payload) => {
    const { data } = await api.put(`/projects/${projectId}/work-tasks/${id}`, payload);
    return data;
  },
  remove: async (projectId, id) => {
    const { data } = await api.delete(`/projects/${projectId}/work-tasks/${id}`);
    return data;
  }
};

// Work report service
export const workReportService = {
  getMonthly: async (userId, year, month) => {
    const { data } = await api.get(`/work-report/${userId}`, { params: { year, month } });
    return data;
  }
};


export default api;
